import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import type { Tournament } from '@shared/schema';
import { Button } from '@/components/ui/button';
import { Eye, Radio, ChevronDown } from 'lucide-react';
import { TournamentLogo } from '@/components/tournament/tournament-logo';
import { isTournamentLive } from '@shared/timeUtils';
import { track } from '@/lib/posthog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

type LiveSummary = {
  liveCount: number;
  inProgressCount: number;
  scheduledTodayCount: number;
};

function readLiveSummary(data: unknown): LiveSummary | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const row = data as Record<string, unknown>;
  const { liveCount, inProgressCount, scheduledTodayCount } = row;
  if (
    typeof liveCount !== 'number' ||
    typeof inProgressCount !== 'number' ||
    typeof scheduledTodayCount !== 'number'
  ) {
    return undefined;
  }
  return { liveCount, inProgressCount, scheduledTodayCount };
}

/** Enough to pick a featured event without a request per public tournament. */
const SUMMARY_CAP = 6;

function startMs(tournament: Tournament): number {
  return new Date(tournament.startDate ?? '').getTime();
}

/**
 * Featured live tournament directly under the landing header.
 * Secondary tournaments collapse to "+N more live" sheet (no inline scroll).
 */
export function LiveNowHero() {
  const [sheetOpen, setSheetOpen] = useState(false);

  const { data: allTournaments } = useQuery<Tournament[]>({
    queryKey: ['/api/public/tournaments'],
    retry: false,
  });

  const dateLive = useMemo(() => {
    if (!allTournaments) return [];
    return allTournaments
      .filter(t => t && t.visibility === 'public')
      .filter(t => isTournamentLive(t.startDate ?? '', t.endDate ?? ''))
      .sort((a, b) => startMs(a) - startMs(b));
  }, [allTournaments]);

  const summaryTargets = dateLive.slice(0, SUMMARY_CAP);

  const summaryQueries = useQueries({
    queries: summaryTargets.map(tournament => ({
      queryKey: ['/api/public/tournaments', tournament.id, 'live-summary'],
      // Game counts move during a weekend; don't wait on the 10 min default.
      staleTime: 60_000,
      retry: false,
    })),
  });

  const progressKey = summaryTargets
    .map((tournament, index) => {
      const count = readLiveSummary(summaryQueries[index]?.data)?.inProgressCount;
      return `${tournament.id}:${typeof count === 'number' ? count : ''}`;
    })
    .join('|');

  const ranked = useMemo(() => {
    const counts = new Map<string, number>();
    for (const part of progressKey.split('|')) {
      if (!part) continue;
      const splitAt = part.lastIndexOf(':');
      const id = part.slice(0, splitAt);
      const raw = part.slice(splitAt + 1);
      counts.set(id, raw === '' ? 0 : Number(raw));
    }
    return [...dateLive].sort((a, b) => {
      const byProgress = (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0);
      if (byProgress !== 0) return byProgress;
      return startMs(a) - startMs(b);
    });
  }, [dateLive, progressKey]);

  const featured = ranked[0];
  const overflow = ranked.slice(1);
  const featuredIndex = summaryTargets.findIndex(tournament => tournament.id === featured?.id);
  const summary =
    featuredIndex >= 0 ? readLiveSummary(summaryQueries[featuredIndex]?.data) : undefined;

  if (!featured) return null;

  const displayName = featured.customName || featured.name;
  const liveLabel =
    summary && summary.inProgressCount > 0
      ? `${summary.inProgressCount} game${summary.inProgressCount === 1 ? '' : 's'} in progress`
      : summary && summary.liveCount > 0
        ? `${summary.liveCount} live on scoreboard`
        : 'Happening now';

  return (
    <section
      className="relative z-10 w-full border-y border-green-500/30 bg-slate-950"
      data-testid="live-now-hero"
    >
      <div className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" aria-hidden />
          <h2 className="font-broadcast text-lg md:text-xl text-white tracking-wide uppercase">
            Live Now
          </h2>
        </div>

        <div className="rounded-xl border border-green-500/40 border-l-4 border-l-[#D4AF37] bg-white/5 backdrop-blur-sm p-3 md:p-4 overflow-hidden">
          <div className="flex items-start gap-3 min-h-0">
            <TournamentLogo tournament={featured} size="sm" />
            <div className="flex-1 min-w-0">
              <h3 className="font-broadcast text-base md:text-lg text-white line-clamp-2">
                {displayName}
              </h3>
              <p className="text-green-400/90 text-sm mt-1 flex items-center gap-1.5">
                <Radio className="w-3.5 h-3.5 shrink-0" />
                {liveLabel}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Link href={`/tournament/${featured.id}`} className="flex-1">
              <Button
                className="w-full h-14 min-h-[56px] touch-manipulation bg-green-600 hover:bg-green-500 text-white font-broadcast tracking-wide"
                data-testid="live-now-hero-cta"
                onClick={() =>
                  track('live_now_hero_clicked', {
                    tournamentId: featured.id,
                    surface: 'directory',
                  })
                }
              >
                <Eye className="w-5 h-5 mr-2" />
                View Live Scoreboard
              </Button>
            </Link>

            {overflow.length > 0 && (
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-14 min-h-[56px] border-green-500/30 text-green-100 touch-manipulation shrink-0"
                    data-testid="live-now-more-chip"
                  >
                    +{overflow.length} more live
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="bg-slate-950 border-green-500/20 max-h-[70vh]"
                >
                  <SheetHeader>
                    <SheetTitle className="font-broadcast text-white">
                      More live tournaments
                    </SheetTitle>
                  </SheetHeader>
                  <ul className="mt-4 space-y-3 overflow-y-auto pb-6">
                    {overflow.map(t => (
                      <li key={t.id}>
                        <Link href={`/tournament/${t.id}`}>
                          <div className="flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:bg-white/5 min-h-16 touch-manipulation">
                            <TournamentLogo tournament={t} size="sm" />
                            <span className="font-medium text-white line-clamp-2">
                              {t.customName || t.name}
                            </span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
